import { CoreApiClient } from '../../core/client'
import { withQuery } from '../../core/query'
import { requirePaymentFactRequest } from '../../payment-fact'
import type { CursorPageResult, PageResult } from '../../core/types'
import type { FulfillmentType } from '../fulfillment'

export interface TenantMallConfig {
  tenantId: number
  mallName: string
  logoUrl: string
  themeColor: string
  bannerTitle: string
  bannerSubtitle: string
  bannerImageUrl: string
  notice: string
  contactPhone: string
  enabled: boolean
  updatedAt: string
}

export interface UpdateTenantMallConfigRequest {
  mallName: string
  logoUrl: string
  themeColor: string
  bannerTitle: string
  bannerSubtitle: string
  bannerImageUrl: string
  notice: string
  contactPhone: string
  enabled: boolean
}

export interface MallDashboard {
  tenantId: number
  merchantCount: number
  categoryCount: number
  productCount: number
  skuCount: number
  orderCount: number
  paidOrderCount: number
  pendingShipmentCount: number
  activeBuyerCount: number
  grossSales: number
  platformCommission: number
}

export interface MallMerchant {
  id: number
  tenantId: number
  merchantName: string
  merchantType: string
  contactName: string
  contactMobile: string
  commissionRate: number
  status: string
  createdAt: string
}

export interface CreateMallMerchantRequest {
  merchantName: string
  merchantType: string
  contactName: string
  contactMobile: string
  commissionRate: number
}

export interface UpdateMallMerchantRequest extends CreateMallMerchantRequest {
  status: string
}

export interface MallCategory {
  id: number
  tenantId: number
  name: string
  parentId?: number | null
  level: number
  sortOrder: number
  enabled: boolean
}

export interface ProductCategory {
  id: number
  tenantId: number
  moduleCode: string
  categoryCode: string
  categoryName: string
  parentId?: number | null
  level: number
  sortOrder: number
  enabled: boolean
  source: string
  createdAt: string
  updatedAt: string
}

export interface CreateProductCategoryRequest {
  tenantId?: number | null
  moduleCode?: string | null
  categoryCode: string
  categoryName: string
  parentId?: number | null
  sortOrder: number
  enabled: boolean
  source?: string | null
}

export interface UpdateProductCategoryRequest {
  categoryName: string
  parentId?: number | null
  sortOrder: number
  enabled: boolean
  source?: string | null
}

export interface ProductBrand {
  id: number
  tenantId: number
  moduleCode: string
  brandCode: string
  brandName: string
  logoUrl: string
  description: string
  enabled: boolean
  source: string
  createdAt: string
  updatedAt: string
}

export interface CreateProductBrandRequest {
  tenantId?: number | null
  moduleCode?: string | null
  brandCode: string
  brandName: string
  logoUrl: string
  description: string
  enabled: boolean
  source?: string | null
}

export interface UpdateProductBrandRequest {
  brandName: string
  logoUrl: string
  description: string
  enabled: boolean
  source?: string | null
}

export interface CreateMallCategoryRequest {
  name: string
  parentId?: number | null
  sortOrder: number
  enabled: boolean
}

export interface UpdateMallCategoryRequest {
  name: string
  sortOrder: number
  enabled: boolean
}

export type MallProductStatus = 'DRAFT' | 'ON_SHELF' | 'OFF_SHELF' | string
export type CatalogProductStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | string
export type CatalogProductSkuStatus = 'ACTIVE' | 'INACTIVE' | string

export interface MallSku {
  id: number
  productId: number
  skuCode: string
  specName: string
  salePrice: number
  marketPrice: number
  stock: number
  weight: number
  enabled: boolean
}

export interface SafeCatalogSkuView {
  id: number
  productId: number
  skuCode: string
  skuName: string
  specValues: string
  specName: string
  price: number
  salePrice: number
  marketPrice: number
  stockQuantity: number
  stock: number
  weight: number
  inventoryMode: string
  fulfillmentType: FulfillmentType
  resourceScope: string
  enabled: boolean
}

export interface CatalogProductSku {
  id: number
  tenantId: number
  moduleCode: string
  productId: number
  skuCode: string
  skuName: string
  specValues: string
  barcode: string
  price: number
  marketPrice: number
  stockQuantity: number
  lockedStockQuantity: number
  weight: number
  inventoryMode: string
  fulfillmentType: FulfillmentType
  resourceScope: string
  fulfillmentConfig: string
  status: CatalogProductSkuStatus
  source: string
  createdAt: string
  updatedAt: string
}

export interface CreateMallSkuRequest {
  skuCode: string
  specName: string
  salePrice: number
  marketPrice: number
  stock: number
  weight: number
  enabled: boolean
}

export interface CreateCatalogProductSkuRequest {
  tenantId?: number | null
  moduleCode?: string | null
  productId?: number | null
  skuCode: string
  skuName: string
  specValues: string
  barcode: string
  price: number
  marketPrice: number
  stockQuantity: number
  weight: number
  inventoryMode?: string
  fulfillmentType?: FulfillmentType
  resourceScope?: string
  fulfillmentConfig?: string
  source?: string | null
}

export interface UpdateMallSkuRequest {
  specName: string
  salePrice: number
  marketPrice: number
  stock: number
  weight: number
  enabled: boolean
}

export interface UpdateCatalogProductSkuRequest {
  skuName: string
  specValues: string
  barcode: string
  price: number
  marketPrice: number
  stockQuantity: number
  weight: number
  inventoryMode?: string
  fulfillmentType?: FulfillmentType
  resourceScope?: string
  fulfillmentConfig?: string
  source?: string | null
}

export interface MallProduct {
  id: number
  tenantId: number
  merchantId: number
  merchantName: string
  categoryId: number
  categoryName: string
  spuCode: string
  title: string
  subtitle: string
  brand: string
  coverImageUrl: string
  status: string
  productType?: string
  inventoryMode?: string
  fulfillmentType?: FulfillmentType
  minSalePrice: number
  totalStock: number
  salesCount: number
  createdAt: string
  skus: MallSku[]
}

export interface SafeCatalogProductView {
  id: number
  tenantId: number
  shopId: number
  merchantId: number
  merchantName: string
  storeId?: number | null
  categoryId?: number | null
  categoryName: string
  spuCode: string
  productName: string
  title: string
  subtitle: string
  brand: string
  mainImageUrl: string
  coverImageUrl: string
  sellingPoint: string
  status: string
  productType: string
  inventoryMode: string
  fulfillmentType: FulfillmentType
  minSalePrice: number
  totalStock: number
  salesCount: number
  createdAt: string | null
  skus: SafeCatalogSkuView[]
}

export interface SafeCatalogProductPage {
  records: SafeCatalogProductView[]
  total: number
  pageNo: number
  pageSize: number
}

export interface CatalogProduct {
  id: number
  tenantId: number
  moduleCode: string
  shopId?: number | null
  productCode: string
  productName: string
  categoryId?: number | null
  brandId?: number | null
  productType: string
  inventoryMode: string
  fulfillmentType: FulfillmentType
  fulfillmentConfig: string
  mainImageUrl: string
  sellingPoint: string
  description: string
  status: CatalogProductStatus
  source: string
  createdAt: string
  updatedAt: string
}

export interface CatalogProductDetail {
  product: CatalogProduct
  category?: ProductCategory | null
  brand?: ProductBrand | null
  skus: CatalogProductSku[]
}

export interface MallProductReviewView {
  tenantId: number
  shopId: number
  productId: number
  reviewStatus: string
  version: number
  submittedBy: number | null
  submittedAt: string | null
  reviewedBy: number | null
  reviewedAt: string | null
  reason: string
  updatedAt: string
}

export interface MallProductReviewPage {
  items: MallProductReviewView[]
  nextCursor: string | null
  hasMore: boolean
}

export interface MallShopFulfillmentConfig {
  tenantId: number
  shopId: number
  allowSharedPoolFallback: boolean
}

export interface CreateMallProductRequest {
  merchantId: number
  categoryId: number
  spuCode: string
  title: string
  subtitle: string
  brand: string
  coverImageUrl: string
  skus: CreateMallSkuRequest[]
}

export interface CreateCatalogProductRequest {
  tenantId?: number | null
  moduleCode?: string | null
  shopId?: number | null
  productCode: string
  productName: string
  categoryId?: number | null
  brandId?: number | null
  productType: string
  inventoryMode?: string
  fulfillmentType?: FulfillmentType
  fulfillmentConfig?: string
  storeIds?: number[]
  mainImageUrl: string
  sellingPoint: string
  description: string
  source?: string | null
}

export interface CreateCatalogProductWithSkuRequest {
  product: CreateCatalogProductRequest
  initialSku: CreateCatalogProductSkuRequest
}

export interface CreateCatalogProductWithSkuResponse {
  product: CatalogProduct
  initialSku: CatalogProductSku
}

export interface UpdateMallProductRequest {
  categoryId: number
  title: string
  subtitle: string
  brand: string
  coverImageUrl: string
  status: MallProductStatus
}

export interface UpdateCatalogProductRequest {
  shopId?: number | null
  productName: string
  categoryId?: number | null
  brandId?: number | null
  productType: string
  inventoryMode?: string
  fulfillmentType?: FulfillmentType
  fulfillmentConfig?: string
  storeIds?: number[]
  mainImageUrl: string
  sellingPoint: string
  description: string
  source?: string | null
}

export interface UpdateMallProductStatusRequest {
  status: MallProductStatus
}

export interface UpdateProductStatusRequest {
  status: CatalogProductStatus
}

export interface UpdateProductSkuStatusRequest {
  status: CatalogProductSkuStatus
}

export interface MallProductQuery {
  tenantId?: number | null
  moduleCode?: string
  merchantId?: number | null
  shopId?: number | null
  storeId?: number | null
  categoryId?: number | null
  keyword?: string
  status?: MallProductStatus | ''
  pageNo: number
  pageSize: number
}

// ==== T11 店铺维护 / 门店工作台 / 库存与履约契约 ====

/** 店铺运费模板规则（与后端 fulfillment-api ShippingTemplate 字段一致）。 */
export interface MallShippingTemplateRule {
  templateId: number
  chargeUnit: 'QUANTITY' | 'WEIGHT'
  firstUnitFee: number
  additionalUnitFee: number
  freeShippingQtyThreshold: number | null
}

/** 运费模板规则写入（skuId 空=商品级，非空=SKU 级覆盖）。 */
export interface MallShippingTemplateRuleInput {
  skuId?: number | null
  chargeUnit?: 'QUANTITY' | 'WEIGHT' | ''
  firstUnitFee: number
  additionalUnitFee?: number | null
  freeShippingQtyThreshold?: number | null
}

/** 门店工作台逐动作能力位（键为后端 MallAction 名称）。 */
export type MallWorkbenchActionMap = Record<string, boolean>

/** 门店工作台 SKU 行（可售开关 + MALL 库存账）。 */
export interface MallStoreWorkbenchSku {
  skuId: number
  enabled: boolean
  availableQty: number
  reservedQty: number
}

/** 门店库存调整后视图。 */
export interface MallStoreStockView {
  skuId: number
  availableQty: number
  reservedQty: number
  inventorySource: 'MALL' | 'ERP'
}

/** 门店库存流水行（V514；quantity 带符号）。 */
export interface MallStoreStockLedgerEntry {
  id: number
  skuId: number
  entryType: 'RESERVE' | 'RELEASE' | 'COMMIT' | 'ADJUST'
  quantity: number
  reason: string | null
  operatorId: number | null
}

// ==== T13 售后 / 营销配置 / 门店运营契约（与 P2 saas 端点字段一一对应） ====

/** 整单售后类型（V515 type 三值 CHECK）。 */
export type MallAfterSaleType = 'REFUND_ONLY' | 'RETURN_REFUND' | 'OVERDUE_AUTO' | string

/**
 * 整单售后状态（V515 status 十值 CHECK）。REVERSAL_PENDING=已分账订单退款被
 * 逆向门禁挂起（逆向分账能力未交付前停在实际可达状态）——工作台必须如实展示，
 * 任何文案不得表述为“退款成功”。
 */
export type MallAfterSaleStatus =
  | 'APPLIED'
  | 'APPROVED'
  | 'REJECTED'
  | 'WAITING_RETURN'
  | 'RECEIVED'
  | 'REFUNDING'
  | 'REFUNDED'
  | 'REFUND_FAILED'
  | 'REVERSAL_PENDING'
  | 'CLOSED'
  | string

/** 售后责任方（V515 fault_party 四值 CHECK；审核时必须显式裁定）。 */
export type MallAfterSaleFaultParty = 'CONSUMER' | 'MERCHANT' | 'PLATFORM' | 'SYSTEM' | string

/** 售后工作台行（后端 AfterSaleView 同构；金额为服务端快照，前端零计算）。 */
export interface MallAfterSaleView {
  id: number
  tenantId: number
  shopId: number
  storeId: number
  orderId: number
  type: MallAfterSaleType
  status: MallAfterSaleStatus
  faultParty: MallAfterSaleFaultParty
  refundProductAmount: number | string | null
  refundShippingAmount: number | string | null
  /** 退款执行（T10）起才写入；REVERSAL_PENDING 时恒 null（如实展示口径）。 */
  refundAmount: number | string | null
  reason: string | null
  auditorId: number | null
  applyAt: string | null
  auditAt: string | null
  receiveAt: string | null
  refundAt: string | null
}

/** 售后审核请求（decision=APPROVE/REJECT；faultParty 必须显式定责）。 */
export interface MallAfterSaleAuditRequest {
  decision: 'APPROVE' | 'REJECT' | string
  faultParty: MallAfterSaleFaultParty
  /** 审核运费：消费者责任必须 0；商家/平台/系统责任 0 ≤ 运费 ≤ 实付−商品款（服务端裁定）。 */
  refundShippingAmount?: number | string | null
}

/** 平台补贴规则视图（后端 SubsidyView 同构；amount/maxAmount 服务端 BigDecimal）。 */
export interface MallPlatformSubsidy {
  id: number | null
  name: string
  scopeType: 'ALL' | 'SHOP' | 'PRODUCT' | string
  shopId: number | null
  productId: number | null
  subsidyType: 'AMOUNT' | 'RATIO' | string
  amount: number | string | null
  ratioBp: number | null
  maxAmount: number | string | null
  validFrom: string | null
  validTo: string | null
  enabled: boolean
}

/** 平台补贴规则写入（(tenant, name) 幂等 upsert；enabled 缺省 true）。 */
export interface MallPlatformSubsidyUpsertRequest {
  name: string
  scopeType?: 'ALL' | 'SHOP' | 'PRODUCT' | string
  shopId?: number | null
  productId?: number | null
  subsidyType?: 'AMOUNT' | 'RATIO' | string
  amount?: number | string | null
  ratioBp?: number | null
  maxAmount?: number | string | null
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
}

/** ERP 库存对账单 SKU 差异（镜像读数 vs ERP 侧读数，均服务端事实）。 */
export interface MallStockReconciliationMismatch {
  skuId: number
  mirrorAvailableQty: number
  mirrorReservedQty: number
  erpAvailableQty: number
  erpReservedQty: number
}

/** ERP 库存对账报告（只读；差异已由服务端落 V515 对账事实）。 */
export interface MallStockReconciliationReport {
  warehouseId: number
  compared: number
  mismatches: MallStockReconciliationMismatch[]
}

/** 门店维度统一订单行（后端 StoreCommonOrder 同构；买家脱敏：无手机号/地址）。 */
export interface MallStoreCommonOrder {
  id: number
  tenantId: number
  orderNo: string
  status: string
  shopId: number | null
  storeId: number | null
  buyerUserId: number | null
  /** 注意：实为收货人姓名（T3 台账口径），UI 命名保持与后端一致。 */
  buyerName: string | null
  totalAmount: number | string | null
  freightAmount: number | string | null
  payAmount: number | string | null
  fulfillmentMode: string | null
  createdAt: string | null
  paidAt: string | null
  completedAt: string | null
  cancelledAt: string | null
}

/** 门店自提/营销配置视图。 */
export interface MallStorePickupConfig {
  pickupDeadlineHours: number
}

export interface MallStorePromotionSwitch {
  promotionsEnabled: boolean
}

/** 门店 ERP 仓库绑定视图（未绑定时 GET 返回 null）。 */
export interface MallStoreWarehouseBinding {
  warehouseId: number
}

/** 自提核销事实（履约层 FulfillmentStatusView 投影；失败如实透出错误，不伪造成功）。 */
export interface MallPickupVerifyFact {
  tenantId: number | null
  moduleCode: string | null
  orderId: number | null
  orderItemId: number | null
  fulfillmentType: string | null
  status: string | null
  detail: string | null
  updatedAt: string | null
  pickupDeadline: string | null
  pickupVerifiedAt: string | null
}

export interface ProductCatalogQuery {
  shopId?: number | null
  categoryId?: number | null
  brandId?: number | null
  productType?: string
  keyword?: string
  status?: CatalogProductStatus | ''
}

export interface AdminMallProduct extends MallProduct {
  tenantCode?: string | null
  tenantName?: string | null
  moduleCode?: string | null
  moduleName?: string | null
  authorized?: boolean
}

export interface CreateAdminProductRequest extends CreateMallProductRequest {
  tenantId?: number | null
  moduleCode?: string | null
}

export interface UpdateAdminProductRequest extends UpdateMallProductRequest {
  tenantId?: number | null
  moduleCode?: string | null
}

export interface MallOrder {
  id: number
  tenantId: number
  orderNo: string
  merchantId: number
  merchantName: string
  buyerUserId?: number | null
  buyerName: string
  productId: number
  productTitle: string
  skuId: number
  skuName: string
  quantity: number
  unitPrice: number
  payAmount: number
  channel: string
  status: string
  receiverName: string
  receiverMobile: string
  receiverAddress: string
  logisticsCompany: string
  logisticsNo: string
  createdAt: string
  paidAt?: string | null
  shippedAt?: string | null
  completedAt?: string | null
}

export type CommonOrderStatus = 'CREATED' | 'PENDING_PAY' | 'PAID' | 'FULFILLING' | 'COMPLETED' | 'CANCELLED' | 'CLOSED'

export type PlatformOrderOperation = 'MARK_PAID' | 'START_FULFILLMENT' | 'CANCEL' | 'COMPLETE'

export interface CommonOrder {
  id: number
  tenantId: number
  moduleCode: string
  orderNo: string
  sourceOrderNo: string
  paymentFactId?: string | null
  idempotencyKey: string
  orderType: string
  bizType: string
  buyerUserId?: number | null
  buyerName: string
  sellerId?: number | null
  sellerName: string
  totalAmount: number
  discountAmount: number
  freightAmount: number
  payAmount: number
  currency: string
  status: CommonOrderStatus
  remark: string
  paidAt?: string | null
  cancelledAt?: string | null
  completedAt?: string | null
  createdAt: string
  updatedAt: string
}

export interface CommonOrderItem {
  id: number
  tenantId: number
  orderId: number
  moduleCode: string
  itemType: string
  productId?: number | null
  skuId?: number | null
  itemCode: string
  itemName: string
  specValues: string
  unitPrice: number
  quantity: number
  totalAmount: number
  remark: string
  createdAt: string
}

export interface CommonOrderDetail {
  order: CommonOrder
  items: CommonOrderItem[]
}

export interface CommonOrderQuery {
  tenantId?: number | null
  moduleCode?: string
  keyword?: string
  status?: CommonOrderStatus | ''
  buyerUserId?: number | null
  sellerId?: number | null
}

export interface AdminCommonOrder extends CommonOrder {
  tenantCode?: string | null
  tenantName?: string | null
  moduleName?: string | null
  supportedOperations: PlatformOrderOperation[]
}

export interface MarkCommonOrderPaidRequest {
  paymentFactId: string
}

export interface CancelCommonOrderRequest {
  reason?: string | null
}

export interface CommonOrderTransaction {
  id: number
  orderId: number
  tenantId: number
  transactionType: string
  amount: number
  status: string
  createdAt: string
  updatedAt: string
}

export interface SubmitMallOrderRequest {
  skuId: number
  quantity: number
  channel: string
  buyerName: string
  receiverName: string
  receiverMobile: string
  receiverAddress: string
  idempotencyKey: string
}

export interface SubmitMallOrderResponse {
  orderId: number
  orderNo: string
  commonOrderId?: number | null
  commonOrderNo?: string | null
  duplicated: boolean
  status: string
  /** P1 T6 起多 SKU 统一轨回填金额快照（服务端 BigDecimal 权威值）；单 SKU 兼容轨为 null。 */
  subtotal?: number | string | null
  shippingFee?: number | string | null
  payableAmount?: number | string | null
}

/** 买家订单 ID：Java Long，客户端一律按字符串传输（避免 TS number 精度丢失）。 */
export type ClientOrderId = number | string

/** 多行统一单的订单行（同店多 SKU 合单）。 */
export interface ClientMallOrderLine {
  skuId: number
  quantity: number
}

/**
 * 买家多行统一单提交（P1 T6/T8）：lines 非空走单店统一订单 prepare
 * （一次只接受一个 shop/store，跨店被服务端拒绝 CROSS_STORE_SUBMISSION_REJECTED；
 * 服务端价权威）。POST /api/client/mall/orders。
 */
export interface ClientSubmitMallOrderRequest {
  lines: ClientMallOrderLine[]
  shopId: number
  storeId: number
  receiverName: string
  receiverMobile: string
  receiverAddress: string
  idempotencyKey: string
  /** DELIVERY（默认）/ PICKUP；自提运费恒 0 但仍必须先选门店。历史值 EXPRESS 由服务端别名兼容为 DELIVERY。 */
  fulfillmentMode?: 'DELIVERY' | 'PICKUP' | string
}

export interface UpdateMallOrderShipmentRequest {
  logisticsCompany: string
  logisticsNo: string
}

export interface MallSettlement {
  tenantId: number
  merchantId: number
  merchantName: string
  orderCount: number
  grossAmount: number
  platformCommission: number
  merchantReceivable: number
  pendingSettleAmount: number
  settledAmount: number
}

export type ShopStatus = 'ACTIVE' | 'SUSPENDED'
export type ShopStoreStatus = 'ACTIVE' | 'SUSPENDED'

export interface Shop {
  id: number
  tenantId: number
  tenantCode?: string | null
  tenantName?: string | null
  moduleCode: string
  moduleName?: string | null
  shopCode: string
  shopName: string
  shopType: string
  ownerName: string
  ownerMobile: string
  contactPhone: string
  logoUrl: string
  address: string
  status: ShopStatus
  source: string
  createdAt: string
  updatedAt: string
}

export interface ShopStore {
  id: number
  tenantId: number
  tenantCode?: string | null
  tenantName?: string | null
  shopId: number
  shopCode?: string | null
  shopName?: string | null
  moduleCode: string
  moduleName?: string | null
  storeCode: string
  storeName: string
  storeType: string
  managerName: string
  managerMobile: string
  contactPhone: string
  province: string
  city: string
  district: string
  address: string
  businessHours: string
  status: ShopStoreStatus
  source: string
  createdAt: string
  updatedAt: string
}

export interface MallWorkbenchShopOption {
  id: number
  name: string
}

export interface MallWorkbenchStoreOption {
  id: number
  shopId: number
  name: string
}

export interface MallWorkbenchScopeOptions {
  shops: MallWorkbenchShopOption[]
  stores: MallWorkbenchStoreOption[]
}

export interface CreateShopRequest {
  tenantId?: number | null
  moduleCode: string
  shopCode: string
  shopName: string
  shopType: string
  ownerName: string
  ownerMobile: string
  contactPhone: string
  logoUrl: string
  address: string
  source?: string | null
}

export interface UpdateShopRequest {
  shopName: string
  shopType: string
  ownerName: string
  ownerMobile: string
  contactPhone: string
  logoUrl: string
  address: string
  source?: string | null
}

export interface UpdateShopStatusRequest {
  status: ShopStatus
}

export interface CreateShopStoreRequest {
  tenantId?: number | null
  shopId: number
  moduleCode: string
  storeCode: string
  storeName: string
  storeType: string
  managerName: string
  managerMobile: string
  contactPhone: string
  province: string
  city: string
  district: string
  address: string
  businessHours: string
  source?: string | null
}

export interface UpdateShopStoreRequest {
  storeName: string
  storeType: string
  managerName: string
  managerMobile: string
  contactPhone: string
  province: string
  city: string
  district: string
  address: string
  businessHours: string
  source?: string | null
}

export interface UpdateShopStoreStatusRequest {
  status: ShopStoreStatus
}

// ==== P3 特殊商品契约（Task 14 PC 消费面；端点由 T4/T7/T9/T10/T12/T2 交付） ====

/**
 * P3 统一订单交易种类（V523 {@code common_order.transaction_kind} 值域）。
 * 组边界：同一次 prepare 内所有行同 kind（STANDARD/PRE_SALE/WEIGH/VIRTUAL/BUNDLE），
 * 服务层矩阵单一权威——前端徽标仅按服务端快照展示。
 */
export type MallTransactionKind = 'STANDARD' | 'PRE_SALE' | 'WEIGH' | 'VIRTUAL' | 'BUNDLE' | string

/** T4 门店 SKU 预售额度视图（后端 PreSaleQuotaView 同构；额度域独立于现货库存）。 */
export interface MallPreSaleQuotaView {
  skuId: number
  totalQty: number
  reservedQty: number
  /** 承诺发货小时数（批次段，prepare 冻结 promise_ship_at 用；服务端上界 8760）。 */
  promiseShipHours: number
}

/** T4 预售配置写入（两字段均必填；promiseShipHours 必须 > 0）。 */
export interface MallPreSaleConfigRequest {
  totalQty: number
  promiseShipHours: number
}

/** T7 门店称重偏差上限视图（null=未配置/回落裁定可见）。 */
export interface MallWeighDeviationView {
  weighDeviationPct: number | null
}

/** T7 称重偏差上限写入（0..100；null=清除门店覆盖）。 */
export interface MallWeighDeviationRequest {
  weighDeviationPct: number | null
}

/**
 * T7 SKU 称重元数据（后端 SkuWeighConfig 同构）。unitPricePerGram 为克单价
 * NUMERIC(10,4)（非称重 SKU 为 null）；行金额=克单价×克重由服务端 BigDecimal HALF_UP
 * 锁定，前端只做展示换算（克/斤/千克常量系数）不做金额计算。
 */
export interface MallSkuWeighConfig {
  skuId: number
  /** NONE/GRAM/JIN/KILOGRAM（T3 谓词权威口径；未知串按 NONE 容错）。 */
  weighUnit: string
  unitPricePerGram: number | string | null
}

/** T7 SKU 称重元数据写入。 */
export interface MallSkuWeighConfigRequest {
  weighUnit: string
  unitPricePerGram: number | string | null
}

/** T12 套装组成行（后端 BundleComponentLine 同构；ID/数量必须 > 0，同套装内不重复）。 */
export interface MallBundleComponentLine {
  componentSkuId: number
  componentQty: number
}

/** T12 套装组成行写入。 */
export interface MallBundleComponentLineRequest {
  componentSkuId: number
  componentQty: number
}

/** T9 门店 SKU 账号权益额度视图（后端 VirtualEntitlementView 同构）。 */
export interface MallVirtualEntitlementView {
  skuId: number
  totalQty: number
  usedQty: number
}

/** T9 权益额度写入。 */
export interface MallVirtualEntitlementConfigRequest {
  totalQty: number
}

/** T10 虚拟交付工作台列表行（脱敏投影：掩码，绝无明文）。 */
export interface MallVirtualDeliveryMaskedView {
  deliveryId: number
  orderId: number
  skuId: number
  /** 卡密（门店池冻结行）/ 账号权益（门店额度消费）。 */
  assetKind: 'CARD_CODE' | 'ENTITLEMENT' | string
  /** PENDING_DELIVERY/DELIVERED/DELIVERY_FAILED/REVOKED。 */
  status: string
  contentMask: string | null
  secretMask: string | null
  deliveredAt: string | null
}

/**
 * T10 虚拟交付敏感明文视图（仅 GET /{deliveryId} 敏感端点返回；content/accessSecret
 * 为卡密明文面——展示需确认交互，泄露面由服务层 VIRTUAL_ASSET_SENSITIVE 收权兜底）。
 */
export interface MallVirtualDeliverySecretView {
  id: number | null
  tenantId: number
  shopId: number
  storeId: number | null
  buyerUserId: number
  orderId: number
  productId: number
  skuId: number
  assetKind: 'CARD_CODE' | 'ENTITLEMENT' | string
  /** CARD_CODE=资产 ID；ENTITLEMENT=skuId（额度桶键，无实例行）。 */
  sourceAssetId: number
  content: string | null
  accessSecret: string | null
  deliveryStatus: string
  failReason: string | null
  deliveredAt: string | null
  revokedBy: number | null
  revokedAt: string | null
  revokeReason: string | null
}

/** T10 撤销请求（原因建议必填留痕）。 */
export interface MallVirtualDeliveryRevokeRequest {
  reason?: string | null
}

/** T10 交付失败重试结果（本次重臂重放实际交付/仍失败的行数）。 */
export interface MallVirtualDeliveryRetryOutcome {
  delivered: number
  failed: number
}

/** T2 分来源优惠投影段（金额 + 承担方 SHOP/PLATFORM/OPERATOR）。 */
export interface MallPromotionBreakdownSegment {
  amount: number | string
  bearer: string
}

/**
 * T2 分来源优惠脱敏投影（后端 MallPromotionBreakdown 同构）：五段金额+承担方，
 * 不透 fulfillmentPlan 原文与活动/券/补贴句柄 ID；无促销快照 present=false 全零段。
 */
export interface MallPromotionBreakdownView {
  present: boolean
  memberPrice: MallPromotionBreakdownSegment
  fullReduce: MallPromotionBreakdownSegment
  coupon: MallPromotionBreakdownSegment
  subsidy: MallPromotionBreakdownSegment
  points: MallPromotionBreakdownSegment
  totalDiscount: number | string
}

/**
 * T2 买家履约事实投影（client 端点；脱敏红线：仅布尔与状态枚举）。
 * virtualDelivery 取值 PENDING_DELIVERY/DELIVERED/DELIVERY_FAILED/REVOKED/NONE。
 */
export interface MallOrderFulfillmentFacts {
  orderId: number
  shipped: boolean
  verified: boolean
  virtualDelivery: string
}

export const MALL_PERMISSIONS = {
  adminInspect: 'platform:mall:inspect',
  workbenchView: 'mall:workbench:view',
  decorationView: 'mall:decoration:view',
  decorationManage: 'mall:decoration:manage',
  decorationComponentView: 'mall:decoration:component:view',
  decorationDatasourceView: 'mall:decoration:datasource:view',
  decorationTemplateView: 'mall:decoration:template:view',
  decorationTemplateManage: 'mall:decoration:template:manage',
  configView: 'mall:config:view',
  configUpdate: 'mall:config:update',
  merchantView: 'mall:merchant:view',
  merchantManage: 'mall:merchant:manage',
  categoryView: 'mall:category:view',
  categoryManage: 'mall:category:manage',
  productView: 'mall:product:view',
  productManage: 'mall:product:manage',
  productReview: 'mall:product:review',
  credentialView: 'mall:credential:view',
  credentialManage: 'mall:credential:manage',
  orderView: 'mall:order:view',
  orderFulfill: 'mall:order:fulfill',
  settlementView: 'mall:settlement:view',
  memberView: 'mall:member:view',
  memberManage: 'mall:member:manage',
  shopView: 'mall:shop:view',
  shopManage: 'mall:shop:manage',
  storeView: 'mall:store:view',
  storeManage: 'mall:store:manage',
  clientConfigView: 'mall:client:config:view',
  clientProductView: 'mall:client:product:view',
  clientDecorationView: 'mall:client:decoration:view',
  clientOrderSubmit: 'mall:client:order:submit',
  clientOrderView: 'mall:client:order:view',
  clientMemberLogin: 'mall:client:member:login'
} as const

export const MALL_PERMISSION_LIST = Object.values(MALL_PERMISSIONS)

/** Mall API methods */
export class MallApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  getSaasProductCategories(query: {
    parentId?: number | null
    rootOnly?: boolean | null
    keyword?: string
    enabled?: boolean | null
  } = {}) {
    return this.client.get<ProductCategory[]>(withQuery('/api/saas/mall/product-categories', query))
  }

  createSaasProductCategory(body: CreateProductCategoryRequest) {
    return this.client.post<ProductCategory>('/api/saas/mall/product-categories', body)
  }

  updateSaasProductCategory(categoryId: number, body: UpdateProductCategoryRequest) {
    return this.client.put<ProductCategory>(`/api/saas/mall/product-categories/${categoryId}`, body)
  }

  updateSaasProductCategoryStatus(categoryId: number, enabled: boolean) {
    return this.client.put<ProductCategory>(`/api/saas/mall/product-categories/${categoryId}/status`, { enabled })
  }

  getSaasProductBrands(query: { keyword?: string; enabled?: boolean | null } = {}) {
    return this.client.get<ProductBrand[]>(withQuery('/api/saas/mall/product-brands', query))
  }

  createSaasProductBrand(body: CreateProductBrandRequest) {
    return this.client.post<ProductBrand>('/api/saas/mall/product-brands', body)
  }

  updateSaasProductBrand(brandId: number, body: UpdateProductBrandRequest) {
    return this.client.put<ProductBrand>(`/api/saas/mall/product-brands/${brandId}`, body)
  }

  updateSaasProductBrandStatus(brandId: number, enabled: boolean) {
    return this.client.put<ProductBrand>(`/api/saas/mall/product-brands/${brandId}/status`, { enabled })
  }

  getSaasProductCatalog(query: ProductCatalogQuery = {}) {
    return this.client.get<CatalogProduct[]>(withQuery('/api/saas/mall/product-catalog', query))
  }

  getSaasMallWorkbenchScopeOptions() {
    return this.client.get<MallWorkbenchScopeOptions>('/api/saas/mall/workbench-scope-options')
  }

  getSaasProductDetail(productId: number) {
    return this.client.get<CatalogProductDetail>(`/api/saas/mall/product-catalog/${productId}`)
  }

  createSaasProductCatalog(body: CreateCatalogProductRequest) {
    return this.client.post<CatalogProduct>('/api/saas/mall/product-catalog', body)
  }

  createSaasProductCatalogWithInitialSku(body: CreateCatalogProductWithSkuRequest) {
    return this.client.post<CreateCatalogProductWithSkuResponse>('/api/saas/mall/product-catalog/composite', body)
  }

  updateSaasProductCatalog(productId: number, body: UpdateCatalogProductRequest) {
    return this.client.put<CatalogProduct>(`/api/saas/mall/product-catalog/${productId}`, body)
  }

  getSaasProductStoreIds(productId: number) {
    return this.client.get<number[]>(`/api/saas/mall/product-catalog/${productId}/stores`)
  }

  replaceSaasProductStoreIds(productId: number, storeIds: number[]) {
    return this.client.put<number[]>(`/api/saas/mall/product-catalog/${productId}/stores`, { storeIds })
  }

  getSaasSubmittedMallProductReviews(shopId: number | null | undefined, cursor: string | null, limit: number) {
    const params: Record<string, unknown> = { cursor, limit }
    if (shopId != null) params.shopId = shopId
    return this.client.get<MallProductReviewPage>(withQuery('/api/saas/mall/product-reviews/submitted-by-me', params))
  }

  getSaasMallProductReviews(shopId?: number | null) {
    const params: Record<string, unknown> = {}
    if (shopId != null) params.shopId = shopId
    return this.client.get<MallProductReviewView[]>(withQuery('/api/saas/mall/product-reviews', params))
  }

  getSaasMallShopFulfillmentConfig(shopId: number, options: { signal?: AbortSignal } = {}) {
    return this.client.get<MallShopFulfillmentConfig>(`/api/saas/mall/shops/${shopId}/fulfillment-config`, options)
  }

  updateSaasMallShopFulfillmentConfig(shopId: number, body: { allowSharedPoolFallback: boolean }) {
    return this.client.put<MallShopFulfillmentConfig>(`/api/saas/mall/shops/${shopId}/fulfillment-config`, body)
  }

  updateSaasProductCatalogStatus(productId: number, body: UpdateProductStatusRequest) {
    return this.client.put<CatalogProduct>(`/api/saas/mall/product-catalog/${productId}/status`, body)
  }

  getSaasProductSkus(productId: number, query: { keyword?: string; status?: CatalogProductSkuStatus | '' } = {}) {
    return this.client.get<CatalogProductSku[]>(withQuery(`/api/saas/mall/product-catalog/${productId}/skus`, query))
  }

  createSaasProductCatalogSku(productId: number, body: CreateCatalogProductSkuRequest) {
    return this.client.post<CatalogProductSku>(`/api/saas/mall/product-catalog/${productId}/skus`, body)
  }

  updateSaasProductCatalogSku(productId: number, skuId: number, body: UpdateCatalogProductSkuRequest) {
    return this.client.put<CatalogProductSku>(`/api/saas/mall/product-catalog/${productId}/skus/${skuId}`, body)
  }

  updateSaasProductCatalogSkuStatus(productId: number, skuId: number, body: UpdateProductSkuStatusRequest) {
    return this.client.put<CatalogProductSku>(`/api/saas/mall/product-catalog/${productId}/skus/${skuId}/status`, body)
  }

  getSaasMallProducts(query: MallProductQuery) {
    return this.client.get<SafeCatalogProductPage>(withQuery('/api/saas/mall/products', query))
  }

  getAdminProducts(query: MallProductQuery) {
    return this.client.get<PageResult<AdminMallProduct>>(withQuery('/api/admin/products', query))
  }

  createAdminProduct(body: CreateAdminProductRequest) {
    return this.client.post<AdminMallProduct>('/api/admin/products', body)
  }

  updateAdminProduct(productId: number, body: UpdateAdminProductRequest) {
    return this.client.put<AdminMallProduct>(`/api/admin/products/${productId}`, body)
  }

  updateAdminProductStatus(productId: number, body: UpdateMallProductStatusRequest) {
    return this.client.put<AdminMallProduct>(`/api/admin/products/${productId}/status`, body)
  }

  createAdminProductSku(productId: number, body: CreateMallSkuRequest) {
    return this.client.post<MallSku>(`/api/admin/products/${productId}/skus`, body)
  }

  updateAdminProductSku(productId: number, skuId: number, body: UpdateMallSkuRequest) {
    return this.client.put<MallSku>(`/api/admin/products/${productId}/skus/${skuId}`, body)
  }

  getAdminOrders(query: CommonOrderQuery & { pageNo: number; pageSize: number }) {
    return this.client.get<PageResult<AdminCommonOrder>>(withQuery('/api/admin/orders', query))
  }

  getAdminOrder(tenantId: number, orderId: number) {
    return this.client.get<CommonOrderDetail>(withQuery(`/api/admin/orders/${orderId}`, { tenantId }))
  }

  markAdminOrderPaid(tenantId: number, orderId: number, body: MarkCommonOrderPaidRequest) {
    return this.client.post<CommonOrder>(
      withQuery(`/api/admin/orders/${orderId}/paid`, { tenantId }),
      requirePaymentFactRequest(body)
    )
  }

  markAdminOrderFulfilling(tenantId: number, orderId: number) {
    return this.client.post<CommonOrder>(withQuery(`/api/admin/orders/${orderId}/fulfilling`, { tenantId }))
  }

  cancelAdminOrder(tenantId: number, orderId: number, body: CancelCommonOrderRequest = {}) {
    return this.client.post<CommonOrder>(withQuery(`/api/admin/orders/${orderId}/cancel`, { tenantId }), body)
  }

  completeAdminOrder(tenantId: number, orderId: number) {
    return this.client.post<CommonOrder>(withQuery(`/api/admin/orders/${orderId}/complete`, { tenantId }))
  }

  /**
   * Saas 全范围订单列表（按操作者 scope 过滤）。T13 评审 I1：不承接门店维度参数——
   * CommonOrderQuery 契约无 shopId/storeId，携带即被服务端 400 拒绝
   * （MALL_ORDER_STORE_FILTER_UNSUPPORTED）；门店维度用 listSaasMallStoreCommonOrders。
   */
  getSaasMallCommonOrders(query: CommonOrderQuery, cursor: string | null, limit: number) {
    return this.client.get<CursorPageResult<CommonOrder>>(withQuery('/api/saas/mall/common-orders', {
      ...query,
      cursor,
      limit,
    }))
  }

  getSaasMallCommonOrder(orderId: number) {
    return this.client.get<CommonOrderDetail>(`/api/saas/mall/common-orders/${orderId}`)
  }

  getSaasMallCommonOrderTransactions(orderId: number) {
    return this.client.get<CommonOrderTransaction[]>(`/api/saas/mall/common-orders/${orderId}/transactions`)
  }

  getSaasMallCommonOrderTransaction(orderId: number, transactionId: number) {
    return this.client.get<CommonOrderTransaction>(`/api/saas/mall/common-orders/${orderId}/transactions/${transactionId}`)
  }

  /** P1 店铺商品：按 ID 读取商品及其真实 SKU 明细。 */
  getMallProduct(productId: number) {
    return this.client.get<CatalogProductDetail>(`/api/saas/mall/product-catalog/${productId}`)
  }

  /** P1 店铺商品分页：商城安全投影（价格/库存/履约方式等安全字段）。 */
  listMallProducts(query: MallProductQuery) {
    return this.client.get<SafeCatalogProductPage>(withQuery('/api/saas/mall/products', query))
  }

  /** P1 店铺 SKU 创建：仅普通实物（服务端校验 P1 商品类型与价格权威）。 */
  createMallSku(productId: number, body: CreateCatalogProductSkuRequest) {
    return this.client.post<CatalogProductSku>(`/api/saas/mall/product-catalog/${productId}/skus`, body)
  }

  /** P1 店铺 SKU 启停：禁用 SKU 服务端拒绝下单准备（MALL_SKU_UNAVAILABLE）。 */
  updateMallSkuStatus(productId: number, skuId: number, body: UpdateProductSkuStatusRequest) {
    return this.client.put<CatalogProductSku>(
      `/api/saas/mall/product-catalog/${productId}/skus/${skuId}/status`,
      body,
    )
  }

  /**
   * P1 T8 买家统一订单游标列表（GET /api/client/mall/common-orders）。
   * 未支付恢复场景传 status: 'PENDING_PAY'；游标为上一页 nextCursor 字符串。
   * limit 缺省 20（后端 CursorPageQuery 必填，缺失被 CURSOR_PAGE_PARAM_INVALID 拒绝）。
   */
  listCommonOrders(
    query: { cursor?: ClientOrderId | null; limit?: number; status?: CommonOrderStatus | '' } = {},
  ) {
    return this.client.get<CursorPageResult<CommonOrder>>(
      withQuery('/api/client/mall/common-orders', {
        ...query,
        limit: query.limit ?? 20,
        cursor: query.cursor == null ? undefined : String(query.cursor),
      }),
    )
  }

  /** P1 T8 买家统一订单详情（含多行 items 投影）；orderId 按字符串传输。 */
  getCommonOrder(orderId: ClientOrderId) {
    return this.client.get<CommonOrderDetail>(
      `/api/client/mall/common-orders/${encodeURIComponent(String(orderId))}`,
    )
  }

  /** P1 T8 买家多行统一单提交（同店多 SKU 合单；服务端价权威）。 */
  submitClientOrder(body: ClientSubmitMallOrderRequest) {
    return this.client.post<SubmitMallOrderResponse>('/api/client/mall/orders', body)
  }

  // ==== T11 PC 店铺维护 / 门店工作台 / 库存与履约 ====

  /** T11 店铺商品绑定运费模板规则（仅店铺管理员；skuId 空=商品级，非空=SKU 级覆盖）。 */
  saveMallSkuShippingTemplate(
    shopId: number,
    productId: number,
    body: MallShippingTemplateRuleInput,
  ) {
    return this.client.put<MallShippingTemplateRule>(
      `/api/saas/mall/shops/${shopId}/products/${productId}/shipping-template`,
      body,
    )
  }

  /** T11 读取商品/SKU 生效运费模板规则（SKU 级未覆盖回落商品级；未绑定返回 null）。 */
  getMallSkuShippingTemplate(shopId: number, productId: number, skuId?: number | null) {
    return this.client.get<MallShippingTemplateRule | null>(
      withQuery(`/api/saas/mall/shops/${shopId}/products/${productId}/shipping-template`, {
        skuId: skuId == null ? undefined : String(skuId),
      }),
    )
  }

  /** T11 解除运费模板绑定（订单行随即形成免费组）。 */
  unbindMallSkuShippingTemplate(shopId: number, productId: number, skuId?: number | null) {
    return this.client.delete<boolean>(
      withQuery(`/api/saas/mall/shops/${shopId}/products/${productId}/shipping-template`, {
        skuId: skuId == null ? undefined : String(skuId),
      }),
    )
  }

  /** T11 门店工作台逐动作能力位投影（角色过滤按钮用；后端服务层收权才是授权）。 */
  listMallWorkbenchActions(shopId: number, storeId?: number | null) {
    return this.client.get<MallWorkbenchActionMap>(
      withQuery('/api/saas/mall/workbench-actions', {
        shopId: String(shopId),
        storeId: storeId == null ? undefined : String(storeId),
      }),
    )
  }

  /** T11 门店可售清单（含 MALL 库存账）。 */
  listMallStoreWorkbenchSkus(shopId: number, storeId: number) {
    return this.client.get<MallStoreWorkbenchSku[]>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/workbench/skus`,
    )
  }

  /** T11 门店 SKU 可售开关（店长动作 MANAGE_AVAILABILITY）。 */
  saveMallStoreSkuAvailability(shopId: number, storeId: number, skuId: number, enabled: boolean) {
    return this.client.put<MallStoreWorkbenchSku>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/workbench/skus/${skuId}/availability`,
      { enabled },
    )
  }

  /** T11 门店库存调整（仓管动作 ADJUST_STOCK；落 V512 账 + V514 ADJUST 流水）。 */
  adjustMallStoreStock(
    shopId: number,
    storeId: number,
    skuId: number,
    body: { delta: number; reason: string },
  ) {
    return this.client.post<MallStoreStockView>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/workbench/skus/${skuId}/stock-adjust`,
      body,
    )
  }

  /** T11 门店库存流水（仓管动作 VIEW_LEDGER；V514 倒序分页）。 */
  listMallStoreStockLedger(shopId: number, storeId: number, limit = 20) {
    return this.client.get<MallStoreStockLedgerEntry[]>(
      withQuery(`/api/saas/mall/shops/${shopId}/stores/${storeId}/workbench/ledger`, { limit }),
    )
  }

  // ==== T13 售后工作台 / 营销配置 / 门店运营 ====

  /** T13 售后列表（storeId 缺省=店铺级范围；岗位收权 AFTER_SALE_HANDLE 在服务层强制）。 */
  listSaasMallAfterSales(
    shopId: number,
    query: { storeId?: number | null; status?: MallAfterSaleStatus | '' ; cursor?: string | null; limit?: number } = {},
  ) {
    return this.client.get<MallAfterSaleView[]>(
      withQuery(`/api/saas/mall/shops/${shopId}/after-sales`, {
        storeId: query.storeId == null ? undefined : String(query.storeId),
        status: query.status || undefined,
        cursor: query.cursor || undefined,
        limit: query.limit ?? 50,
      }),
    )
  }

  /** T13 售后审核（通过/驳回；faultParty 必须显式定责，运费规则服务端裁定）。 */
  auditSaasMallAfterSale(shopId: number, afterSaleId: number, body: MallAfterSaleAuditRequest) {
    return this.client.post<MallAfterSaleView>(
      `/api/saas/mall/shops/${shopId}/after-sales/${afterSaleId}/audit`,
      body,
    )
  }

  /** T13 退货收货确认（WAITING_RETURN → RECEIVED，退款执行衔接点）。 */
  confirmSaasMallAfterSaleReceive(shopId: number, afterSaleId: number) {
    return this.client.post<MallAfterSaleView>(
      `/api/saas/mall/shops/${shopId}/after-sales/${afterSaleId}/receive-confirm`,
      {},
    )
  }

  /**
   * P2 最终评审 I2 修复：人工退款执行（T10 exec 薄壳端点）。
   * 服务端 REFUND_MUTATE 岗位收权 + 范围防探；失败原样透出（403/4xx），不伪造成功。
   */
  execSaasMallAfterSaleRefund(shopId: number, afterSaleId: number) {
    return this.client.post<MallAfterSaleView>(
      `/api/saas/mall/shops/${shopId}/after-sales/${afterSaleId}/refund-exec`,
      {},
    )
  }

  /** T13 平台补贴规则列表（租户级启用规则）。 */
  listSaasMallPlatformSubsidies() {
    return this.client.get<MallPlatformSubsidy[]>('/api/saas/mall/promotion-config/subsidies')
  }

  /** T13 平台补贴规则保存（(tenant, name) 幂等 upsert）。 */
  saveSaasMallPlatformSubsidy(body: MallPlatformSubsidyUpsertRequest) {
    return this.client.put<MallPlatformSubsidy>('/api/saas/mall/promotion-config/subsidies', body)
  }

  /** T13 平台补贴规则停用（不删除，历史快照不受影响）。 */
  disableSaasMallPlatformSubsidy(name: string) {
    return this.client.delete<boolean>(
      `/api/saas/mall/promotion-config/subsidies/${encodeURIComponent(name)}`,
    )
  }

  /** T13 店铺营销开关读取（无配置行=默认开启）。 */
  getSaasMallStorePromotionSwitch(shopId: number, storeId: number) {
    return this.client.get<MallStorePromotionSwitch>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/promotion-switch`,
    )
  }

  /** T13 店铺营销开关设置（店长；仅影响后续新订单试算）。 */
  saveSaasMallStorePromotionSwitch(shopId: number, storeId: number, promotionsEnabled: boolean) {
    return this.client.put<MallStorePromotionSwitch>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/promotion-switch`,
      { promotionsEnabled },
    )
  }

  /** T13 门店自提取货期限读取（小时；无配置行=默认 168）。 */
  getSaasMallStorePickupConfig(shopId: number, storeId: number) {
    return this.client.get<MallStorePickupConfig>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/pickup-config`,
    )
  }

  /** T13 门店自提取货期限设置（1..8760 小时；店长；随订单冻结不追溯）。 */
  saveSaasMallStorePickupConfig(shopId: number, storeId: number, pickupDeadlineHours: number) {
    return this.client.put<MallStorePickupConfig>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/pickup-config`,
      { pickupDeadlineHours },
    )
  }

  /** T13 门店 ERP 仓库绑定读取（未绑定返回 null）。 */
  getSaasMallStoreWarehouseBinding(shopId: number, storeId: number) {
    return this.client.get<MallStoreWarehouseBinding | null>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/warehouse-binding`,
    )
  }

  /** T13 门店绑定 ERP 仓库（一店一仓；仓管/店铺管理员 ERP_SYNC）。 */
  bindSaasMallStoreWarehouse(shopId: number, storeId: number, warehouseId: number) {
    return this.client.put<MallStoreWarehouseBinding>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/warehouse-binding`,
      { warehouseId },
    )
  }

  /** T13 门店解除 ERP 仓库绑定。 */
  unbindSaasMallStoreWarehouse(shopId: number, storeId: number) {
    return this.client.delete<boolean>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/warehouse-binding`,
    )
  }

  /** T13 ERP 库存对账（只读报告；差异服务端已落对账事实）。 */
  getSaasMallStockReconciliation(shopId: number, storeId: number) {
    return this.client.get<MallStockReconciliationReport>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/stock-reconciliation`,
    )
  }

  /** T13 门店维度统一订单（仅 MALL_UNIFIED 轨；与 legacy /common-orders 两轨合并由消费方显式完成）。 */
  listSaasMallStoreCommonOrders(
    shopId: number,
    storeId: number,
    query: { status?: string | ''; cursor?: string | null; limit?: number } = {},
  ) {
    return this.client.get<CursorPageResult<MallStoreCommonOrder>>(
      withQuery(`/api/saas/mall/shops/${shopId}/stores/${storeId}/common-orders`, {
        status: query.status || undefined,
        cursor: query.cursor || undefined,
        limit: query.limit ?? 20,
      }),
    )
  }

  /** T13 自提到店核销（薄壳；岗位收权/闸门/订单锁在服务端门面，失败如实透出错误码）。 */
  verifySaasMallPickup(shopId: number, storeId: number, orderId: number, operationId: string) {
    return this.client.post<MallPickupVerifyFact>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/orders/${orderId}/pickup-verify`,
      { operationId },
    )
  }

  // ==== P3 特殊商品管理与投影消费（Task 14；端点由 T4/T7/T9/T10/T12/T2 交付） ====

  /** T4 读取门店 SKU 预售额度（total/reserved/承诺发货小时；未配置以稳定错误码拒绝）。 */
  getSaasMallPreSaleConfig(shopId: number, storeId: number, skuId: number) {
    return this.client.get<MallPreSaleQuotaView>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/pre-sale/${skuId}`,
    )
  }

  /**
   * T4 设置门店 SKU 预售总额度与承诺发货小时（SPECIAL_GOODS_MANAGE 收权；
   * 调整下限不得低于已预占由服务端裁定，403/4xx 原样透出）。
   */
  saveSaasMallPreSaleConfig(
    shopId: number,
    storeId: number,
    skuId: number,
    body: MallPreSaleConfigRequest,
  ) {
    return this.client.put<MallPreSaleQuotaView>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/pre-sale/${skuId}`,
      body,
    )
  }

  /** T7 读取门店称重偏差上限（null=未配置/回落裁定）。 */
  getSaasMallWeighDeviation(shopId: number, storeId: number) {
    return this.client.get<MallWeighDeviationView>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/weigh-config`,
    )
  }

  /** T7 设置门店称重偏差上限（0..100；null=清除门店覆盖；店长收权）。 */
  saveSaasMallWeighDeviation(shopId: number, storeId: number, body: MallWeighDeviationRequest) {
    return this.client.put<MallWeighDeviationView>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/weigh-config`,
      body,
    )
  }

  /** T7 读取 SKU 称重元数据（weigh_unit/克单价/展示换算系数；未知 SKU 稳定错误码拒绝）。 */
  getSaasMallSkuWeighConfig(shopId: number, storeId: number, skuId: number) {
    return this.client.get<MallSkuWeighConfig>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/weigh-config/${skuId}`,
    )
  }

  /** T7 设置 SKU 称重元数据（单位值域 NONE/GRAM/JIN/KILOGRAM；克单价 NUMERIC(10,4)；店长收权）。 */
  saveSaasMallSkuWeighConfig(
    shopId: number,
    storeId: number,
    skuId: number,
    body: MallSkuWeighConfigRequest,
  ) {
    return this.client.put<MallSkuWeighConfig>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/weigh-config/${skuId}`,
      body,
    )
  }

  /** T12 读取套装组成（按组成 SKU 升序；无组成返回空表）。 */
  listSaasMallBundleComponents(shopId: number, skuId: number) {
    return this.client.get<MallBundleComponentLine[]>(
      `/api/saas/mall/shops/${shopId}/bundles/${skuId}/components`,
    )
  }

  /**
   * T12 整体替换套装组成（校验+收权在服务层：子 SKU 全 PHYSICAL/同店/履约一致/嵌套禁令，
   * 违反 MALL_BUNDLE_COMPONENT_INVALID；任何校验失败零残留，失败原样透出）。
   */
  saveSaasMallBundleComponents(
    shopId: number,
    skuId: number,
    lines: MallBundleComponentLineRequest[],
  ) {
    return this.client.put<MallBundleComponentLine[]>(
      `/api/saas/mall/shops/${shopId}/bundles/${skuId}/components`,
      lines,
    )
  }

  /** T12 清空套装组成（幂等；SKU 回落普通实物语义）。 */
  clearSaasMallBundleComponents(shopId: number, skuId: number) {
    return this.client.delete<boolean>(
      `/api/saas/mall/shops/${shopId}/bundles/${skuId}/components`,
    )
  }

  /** T9 读取门店 SKU 账号权益额度（total/used/余量；未配置以稳定错误码拒绝）。 */
  getSaasMallVirtualEntitlementQuota(shopId: number, storeId: number, skuId: number) {
    return this.client.get<MallVirtualEntitlementView>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/entitlement-quota/${skuId}`,
    )
  }

  /** T9 设置门店 SKU 权益总额度（调整下限不得低于已用由服务端裁定；SPECIAL_GOODS_MANAGE 收权）。 */
  saveSaasMallVirtualEntitlementQuota(
    shopId: number,
    storeId: number,
    skuId: number,
    body: MallVirtualEntitlementConfigRequest,
  ) {
    return this.client.put<MallVirtualEntitlementView>(
      `/api/saas/mall/shops/${shopId}/stores/${storeId}/entitlement-quota/${skuId}`,
      body,
    )
  }

  /**
   * T10 虚拟交付工作台列表（脱敏投影：掩码不含明文；storeId 缺省=店铺级覆盖全部门店，
   * status=DELIVERY_FAILED 即异常列表；跨店行以稳定 404 拒绝不泄露存在性）。
   */
  listSaasMallVirtualDeliveries(
    shopId: number,
    query: { storeId?: number | null; status?: string | ''; cursor?: string | number | null; limit?: number } = {},
  ) {
    return this.client.get<MallVirtualDeliveryMaskedView[]>(
      withQuery(`/api/saas/mall/shops/${shopId}/virtual-deliveries`, {
        storeId: query.storeId == null ? undefined : String(query.storeId),
        status: query.status || undefined,
        cursor: query.cursor == null || query.cursor === '' ? undefined : String(query.cursor),
        limit: query.limit ?? 20,
      }),
    )
  }

  /**
   * T10 交付内容敏感明文查看（卡密明文/权益固定文案；VIRTUAL_ASSET_SENSITIVE 服务层收权，
   * 403 原样透出——本端点是唯一明文来源，列表投影恒为掩码）。
   */
  viewSaasMallVirtualDeliverySecret(shopId: number, deliveryId: number) {
    return this.client.get<MallVirtualDeliverySecretView>(
      `/api/saas/mall/shops/${shopId}/virtual-deliveries/${deliveryId}`,
    )
  }

  /** T10 敏感撤销（DELIVERED → REVOKED；卡密作废/权益回补额度；VIRTUAL_ASSET_SENSITIVE）。 */
  revokeSaasMallVirtualDelivery(
    shopId: number,
    deliveryId: number,
    body: MallVirtualDeliveryRevokeRequest,
  ) {
    return this.client.post<MallVirtualDeliverySecretView>(
      `/api/saas/mall/shops/${shopId}/virtual-deliveries/${deliveryId}/revoke`,
      body,
    )
  }

  /** T10 敏感补发（卡密专属：原交付 REVOKED + 门店池补冻差额并交付新卡；全有或全无）。 */
  reissueSaasMallVirtualDelivery(shopId: number, deliveryId: number) {
    return this.client.post<MallVirtualDeliverySecretView>(
      `/api/saas/mall/shops/${shopId}/virtual-deliveries/${deliveryId}/reissue`,
      {},
    )
  }

  /** T10 交付失败重试（DELIVERY_FAILED → 重臂 → 重新解析；零池消费，重试同单不重复消耗池）。 */
  retrySaasMallVirtualDelivery(shopId: number, deliveryId: number) {
    return this.client.post<MallVirtualDeliveryRetryOutcome>(
      `/api/saas/mall/shops/${shopId}/virtual-deliveries/${deliveryId}/retry`,
      {},
    )
  }

  /**
   * T2 分来源优惠脱敏投影（saas）：五段金额+承担方，不透 fulfillmentPlan 原文与活动/券/
   * 补贴句柄 ID（脱敏红线）；无促销快照返回 present=false 全零段。
   */
  getSaasMallPromotionBreakdown(orderId: number) {
    return this.client.get<MallPromotionBreakdownView>(
      `/api/saas/mall/common-orders/${orderId}/promotion-breakdown`,
    )
  }

  /**
   * T2 买家履约事实投影（client）：售后类型预判与虚拟交付状态的事实依据；
   * virtualDelivery 取值 PENDING_DELIVERY/DELIVERED/DELIVERY_FAILED/REVOKED/NONE。
   */
  getCommonOrderFulfillmentFacts(orderId: ClientOrderId) {
    return this.client.get<MallOrderFulfillmentFacts>(
      `/api/client/mall/common-orders/${encodeURIComponent(String(orderId))}/fulfillment-facts`,
    )
  }

}
